import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../context/NotificationContext';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import ChangeLog from '../../../components/ChangeLog';
import AdminPagination from '../../components/AdminPagination';
import CancelOrderModal from '../../../components/CancelOrderModal';
import './Orders.css';

const PAGINATION_MOBILE_BREAKPOINT = 768;
const ITEMS_PER_PAGE_DESKTOP = 10;
const ITEMS_PER_PAGE_MOBILE = 5;

const Orders = () => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useNotification();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_DESKTOP);
  const [search, setSearch] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [timeFilter, setTimeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [restaurantId, setRestaurantId] = useState('');
  const [restaurants, setRestaurants] = useState([]);
  const [stores, setStores] = useState([]);

  useEffect(() => {
    fetchOrders();
  }, [searchApplied, timeFilter, dateFrom, dateTo, restaurantId]);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const [rRes, sRes] = await Promise.all([
          axios.get(`${API_URL}/admin/restaurants`),
          axios.get(`${API_URL}/admin/stores`)
        ]);
        setRestaurants(rRes.data);
        setStores(sRes.data);
      } catch (e) {
        console.error('Error fetching venues:', e);
      }
    };
    fetchVenues();
  }, []);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${PAGINATION_MOBILE_BREAKPOINT}px)`);
    const handler = () => setItemsPerPage(mql.matches ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE_DESKTOP);
    handler();
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchApplied) params.search = searchApplied;
      if (timeFilter) params.timeFilter = timeFilter;
      if (timeFilter === 'custom' && dateFrom && dateTo) {
        params.dateFrom = dateFrom;
        params.dateTo = dateTo;
      }
      if (restaurantId) params.restaurantId = restaurantId;
      const response = await axios.get(`${API_URL}/admin/orders`, { params });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const applySearch = () => {
    setSearchApplied(search.trim());
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setSearchApplied('');
    setTimeFilter('');
    setDateFrom('');
    setDateTo('');
    setRestaurantId('');
    setCurrentPage(1);
  };

  const handleView = async (orderId) => {
    try {
      const response = await axios.get(`${API_URL}/admin/orders/${orderId}`);
      setSelectedOrder(response.data);
    } catch (error) {
      console.error('Error fetching order details:', error);
      showError('Error loading order details');
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedOrder || updatingStatus) return;
    if (newStatus === 'cancelled') {
      setShowCancelModal(true);
      return;
    }
    setUpdatingStatus(true);
    try {
      await axios.patch(`${API_URL}/admin/orders/${selectedOrder._id}/status`, {
        status: newStatus
      });
      const updatedOrder = { ...selectedOrder, status: newStatus };
      setSelectedOrder(updatedOrder);
      setOrders(orders.map(order =>
        order._id === selectedOrder._id ? updatedOrder : order
      ));
      showSuccess(t('admin.statusUpdated'));
    } catch (error) {
      console.error('Error updating order status:', error);
      showError(t('admin.statusUpdateFailed'));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleCancelOrderConfirm = async (cancellationMessage) => {
    if (!selectedOrder || updatingStatus) return;
    setUpdatingStatus(true);
    try {
      const res = await axios.patch(`${API_URL}/admin/orders/${selectedOrder._id}/status`, {
        status: 'cancelled',
        cancellationMessage
      });
      const updatedOrder = res.data;
      setSelectedOrder(updatedOrder);
      setOrders(orders.map(order =>
        order._id === selectedOrder._id ? updatedOrder : order
      ));
      setShowCancelModal(false);
      showSuccess(t('admin.statusUpdated'));
    } catch (error) {
      console.error('Error cancelling order:', error);
      showError(t('admin.statusUpdateFailed'));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatItemVariations = (item) => {
    if (!item.selectedVariations) return '';
    
    let variationsText = '';
    if (Array.isArray(item.selectedVariations)) {
      variationsText = item.selectedVariations.map(variation => {
        if (variation.selectedOptions && Array.isArray(variation.selectedOptions)) {
          return `${variation.name}: ${variation.selectedOptions.join(', ')}`;
        }
        return variation.name;
      }).join(' | ');
    } else if (typeof item.selectedVariations === 'object') {
      variationsText = Object.entries(item.selectedVariations).map(([name, options]) => {
        if (Array.isArray(options)) {
          return `${name}: ${options.join(', ')}`;
        }
        return `${name}: ${options}`;
      }).join(' | ');
    }
    
    return variationsText ? ` (${variationsText})` : '';
  };

  const printA4 = () => {
    if (!selectedOrder) return;
    
    const printWindow = window.open('', '_blank');
    const receiptContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Order Receipt - ${selectedOrder.orderNumber}</title>
          <style>
            @media print {
              @page { size: A4; margin: 20mm; }
            }
            body {
              font-family: Arial, sans-serif;
              max-width: 210mm;
              margin: 0 auto;
              padding: 20px;
              font-size: 14px;
              color: #000;
            }
            .receipt-header {
              text-align: center;
              border-bottom: 2px solid #000;
              padding-bottom: 15px;
              margin-bottom: 20px;
            }
            .receipt-header h1 {
              margin: 0 0 10px 0;
              font-size: 24px;
            }
            .receipt-section {
              margin-bottom: 20px;
            }
            .receipt-section h3 {
              margin: 0 0 10px 0;
              font-size: 16px;
              border-bottom: 1px solid #ccc;
              padding-bottom: 5px;
            }
            .receipt-row {
              display: flex;
              justify-content: space-between;
              margin: 5px 0;
              padding: 3px 0;
            }
            .receipt-row-label {
              font-weight: 600;
              width: 150px;
            }
            .receipt-items {
              margin: 10px 0;
            }
            .receipt-item {
              margin: 8px 0;
              padding: 8px;
              background: #f5f5f5;
              border-radius: 4px;
            }
            .receipt-item-name {
              font-weight: 600;
              margin-bottom: 4px;
            }
            .receipt-item-variations {
              font-size: 12px;
              color: #666;
              margin: 4px 0;
            }
            .receipt-item-details {
              display: flex;
              justify-content: space-between;
              margin-top: 4px;
            }
            .receipt-total {
              border-top: 2px solid #000;
              margin-top: 20px;
              padding-top: 15px;
              font-weight: bold;
            }
            .receipt-total-row {
              display: flex;
              justify-content: space-between;
              margin: 8px 0;
              font-size: 16px;
            }
            .receipt-total-final {
              font-size: 20px;
              border-top: 1px solid #000;
              padding-top: 10px;
              margin-top: 10px;
            }
            .receipt-footer {
              margin-top: 30px;
              padding-top: 15px;
              border-top: 1px solid #ccc;
              text-align: center;
              font-size: 12px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <div class="receipt-header">
            <h1>${selectedOrder.restaurantName || 'Order Receipt'}</h1>
            <p><strong>Order Number:</strong> ${selectedOrder.orderNumber}</p>
            <p><strong>Date:</strong> ${new Date(selectedOrder.createdAt).toLocaleString('en-GB')}</p>
            <p><strong>Type:</strong> ${selectedOrder.restaurantType === 'Store' ? 'Store' : 'Restaurant'}</p>
          </div>

          <div class="receipt-section">
            <h3>Customer Information</h3>
            <div class="receipt-row">
              <span class="receipt-row-label">Name:</span>
              <span>${selectedOrder.customerName || (selectedOrder.user ? `${selectedOrder.user.firstName || ''} ${selectedOrder.user.lastName || ''}`.trim() : '') || 'N/A'}</span>
            </div>
            <div class="receipt-row">
              <span class="receipt-row-label">Email:</span>
              <span>${selectedOrder.customerEmail || selectedOrder.user?.email || 'N/A'}</span>
            </div>
            <div class="receipt-row">
              <span class="receipt-row-label">Phone:</span>
              <span>${selectedOrder.customerPhone ?? selectedOrder.user?.phoneNumber ?? 'N/A'}</span>
            </div>
          </div>

          ${selectedOrder.deliveryAddress ? `
            <div class="receipt-section">
              <h3>Delivery Address</h3>
              <div class="receipt-row">
                <span class="receipt-row-label">Address:</span>
                <span>${selectedOrder.deliveryAddress.street || ''}</span>
              </div>
              <div class="receipt-row">
                <span class="receipt-row-label">City:</span>
                <span>${selectedOrder.deliveryAddress.city || ''}, ${selectedOrder.deliveryAddress.zipCode || ''}</span>
              </div>
              ${selectedOrder.deliveryAddress.country ? `
                <div class="receipt-row">
                  <span class="receipt-row-label">Country:</span>
                  <span>${selectedOrder.deliveryAddress.country}</span>
                </div>
              ` : ''}
            </div>
          ` : ''}

          <div class="receipt-section">
            <h3>Order Items</h3>
            <div class="receipt-items">
              ${selectedOrder.items?.map((item, idx) => `
                <div class="receipt-item">
                  <div class="receipt-item-name">${item.name} x${item.quantity}</div>
                  ${formatItemVariations(item) ? `
                    <div class="receipt-item-variations">${formatItemVariations(item).replace(/^ \(/, '').replace(/\)$/, '')}</div>
                  ` : ''}
                  ${(item.selectedIngredients && item.selectedIngredients.length) ? `
                    <div class="receipt-item-variations">Ingredients: ${item.selectedIngredients.join(', ')}</div>
                  ` : ''}
                  ${item.description ? `
                    <div class="receipt-item-variations">${item.description}</div>
                  ` : ''}
                  <div class="receipt-item-details">
                    <span>Unit Price: €${item.price?.toFixed(2) || '0.00'}</span>
                    <span><strong>Total: €${(item.price * item.quantity)?.toFixed(2) || '0.00'}</strong></span>
                  </div>
                </div>
              `).join('') || '<p>No items</p>'}
            </div>
          </div>

          <div class="receipt-section">
            <h3>Pricing Breakdown</h3>
            <div class="receipt-total">
              <div class="receipt-total-row">
                <span>Subtotal (before tax):</span>
                <span>€${selectedOrder.subtotalBeforeTax?.toFixed(2) || '0.00'}</span>
              </div>
              ${selectedOrder.taxAmount > 0 ? `
                <div class="receipt-total-row">
                  <span>Tax (${selectedOrder.taxRate}%):</span>
                  <span>€${selectedOrder.taxAmount?.toFixed(2)}</span>
                </div>
              ` : ''}
              <div class="receipt-total-row">
                <span>Subtotal:</span>
                <span>€${selectedOrder.subtotal?.toFixed(2) || '0.00'}</span>
              </div>
              <div class="receipt-total-row">
                <span>Delivery Fee:</span>
                <span>€${selectedOrder.deliveryFee?.toFixed(2) || '0.00'}</span>
              </div>
              ${selectedOrder.serviceFee > 0 ? `
                <div class="receipt-total-row">
                  <span>Service Fee${selectedOrder.serviceFeeRate ? ` (${selectedOrder.serviceFeeRate.toFixed(2)}%)` : ''}:</span>
                  <span>€${selectedOrder.serviceFee?.toFixed(2)}</span>
                </div>
              ` : ''}
              ${selectedOrder.discountAmount > 0 ? `
                <div class="receipt-total-row" style="color: #27ae60;">
                  <span>Discount:</span>
                  <span>-€${selectedOrder.discountAmount?.toFixed(2)}</span>
                </div>
              ` : ''}
              <div class="receipt-total-row receipt-total-final">
                <span><strong>TOTAL:</strong></span>
                <span><strong>€${((selectedOrder.total || 0) + (selectedOrder.walletAmount || 0)).toFixed(2)}</strong></span>
              </div>
            </div>
          </div>

          <div class="receipt-section">
            <h3>Order Details</h3>
            <div class="receipt-row">
              <span class="receipt-row-label">Delivery Method:</span>
              <span>${selectedOrder.deliveryMethod === 'delivery' ? 'Delivery' : selectedOrder.deliveryMethod === 'pickup' ? 'Pickup' : 'Eat In'}</span>
            </div>
            <div class="receipt-row">
              <span class="receipt-row-label">Payment Method:</span>
              <span>${selectedOrder.paymentMethod || 'N/A'}</span>
            </div>
            <div class="receipt-row">
              <span class="receipt-row-label">Payment Status:</span>
              <span>${selectedOrder.paymentStatus || 'N/A'}</span>
            </div>
            <div class="receipt-row">
              <span class="receipt-row-label">Order Status:</span>
              <span>${selectedOrder.status || 'N/A'}</span>
            </div>
            ${selectedOrder.deliveryDriver ? `
              <div class="receipt-row">
                <span class="receipt-row-label">Delivery Driver:</span>
                <span>${selectedOrder.deliveryDriver.firstName || ''} ${selectedOrder.deliveryDriver.lastName || ''}</span>
              </div>
            ` : ''}
            ${selectedOrder.deliveryDistance > 0 ? `
              <div class="receipt-row">
                <span class="receipt-row-label">Delivery Distance:</span>
                <span>${selectedOrder.deliveryDistance.toFixed(2)} km</span>
              </div>
            ` : ''}
          </div>

          <div class="receipt-footer">
            <p>Thank you for your order!</p>
            <p>Generated on ${new Date().toLocaleString('en-GB')}</p>
          </div>
        </body>
      </html>
    `;
    printWindow.document.write(receiptContent);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const printReceipt = () => {
    if (!selectedOrder) return;
    
    const printWindow = window.open('', '_blank');
    const receiptContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt - ${selectedOrder.orderNumber}</title>
          <style>
            @media print {
              @page { size: 80mm auto; margin: 0; }
              body { margin: 0; padding: 10px; font-size: 11px; }
            }
            body {
              font-family: 'Courier New', monospace;
              width: 80mm;
              margin: 0 auto;
              padding: 10px;
              font-size: 11px;
            }
            .receipt-header {
              text-align: center;
              border-bottom: 1px dashed #000;
              padding-bottom: 8px;
              margin-bottom: 8px;
            }
            .receipt-header h2 {
              margin: 0 0 5px 0;
              font-size: 14px;
            }
            .receipt-section {
              margin: 8px 0;
              padding: 5px 0;
              border-bottom: 1px dashed #ccc;
            }
            .receipt-section-title {
              font-weight: bold;
              margin-bottom: 3px;
              font-size: 10px;
            }
            .receipt-row {
              font-size: 10px;
              margin: 2px 0;
            }
            .receipt-item {
              display: flex;
              justify-content: space-between;
              margin: 4px 0;
              padding: 3px 0;
            }
            .receipt-item-name {
              font-weight: bold;
              margin-bottom: 2px;
            }
            .receipt-item-variations {
              font-size: 9px;
              color: #555;
              margin: 2px 0;
            }
            .receipt-total {
              border-top: 1px dashed #000;
              margin-top: 10px;
              padding-top: 8px;
              font-weight: bold;
            }
            .receipt-total-row {
              display: flex;
              justify-content: space-between;
              margin: 3px 0;
            }
            .receipt-footer {
              margin-top: 10px;
              text-align: center;
              font-size: 9px;
              border-top: 1px dashed #000;
              padding-top: 8px;
            }
          </style>
        </head>
        <body>
          <div class="receipt-header">
            <h2>${selectedOrder.restaurantName || 'Order'}</h2>
            <p>Order #: ${selectedOrder.orderNumber}</p>
            <p>${new Date(selectedOrder.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
            <p>${selectedOrder.restaurantType === 'Store' ? 'Store' : 'Restaurant'}</p>
          </div>

          <div class="receipt-section">
            <div class="receipt-section-title">CUSTOMER</div>
            <div class="receipt-row">${selectedOrder.customerName || (selectedOrder.user ? `${selectedOrder.user.firstName || ''} ${selectedOrder.user.lastName || ''}`.trim() : '') || 'N/A'}</div>
            <div class="receipt-row">${selectedOrder.customerEmail || selectedOrder.user?.email || 'N/A'}</div>
            ${(selectedOrder.customerPhone ?? selectedOrder.user?.phoneNumber) ? `
              <div class="receipt-row">${selectedOrder.customerPhone ?? selectedOrder.user?.phoneNumber}</div>
            ` : ''}
          </div>

          ${selectedOrder.deliveryAddress ? `
            <div class="receipt-section">
              <div class="receipt-section-title">DELIVERY ADDRESS</div>
              <div class="receipt-row">${selectedOrder.deliveryAddress.street || ''}</div>
              ${selectedOrder.deliveryAddress.addressDetails ? `<div class="receipt-row">${selectedOrder.deliveryAddress.addressDetails}</div>` : ''}
              <div class="receipt-row">${selectedOrder.deliveryAddress.city || ''}, ${selectedOrder.deliveryAddress.zipCode || ''}</div>
            </div>
          ` : ''}

          <div class="receipt-section">
            <div class="receipt-section-title">ITEMS</div>
            ${selectedOrder.items?.map(item => `
              <div class="receipt-item">
                <div style="flex: 1;">
                  <div class="receipt-item-name">${item.name} x${item.quantity}</div>
                  ${formatItemVariations(item) ? `
                    <div class="receipt-item-variations">${formatItemVariations(item).replace(/^ \(/, '').replace(/\)$/, '')}</div>
                  ` : ''}
                  ${(item.selectedIngredients && item.selectedIngredients.length) ? `
                    <div class="receipt-item-variations">Ingredients: ${item.selectedIngredients.join(', ')}</div>
                  ` : ''}
                </div>
                <div style="text-align: right;">
                  <div>€${item.price?.toFixed(2)}</div>
                  <div><strong>€${(item.price * item.quantity)?.toFixed(2)}</strong></div>
                </div>
              </div>
            `).join('') || ''}
          </div>

          <div class="receipt-total">
            <div class="receipt-total-row">
              <span>Subtotal:</span>
              <span>€${selectedOrder.subtotalBeforeTax?.toFixed(2) || '0.00'}</span>
            </div>
            ${selectedOrder.taxAmount > 0 ? `
              <div class="receipt-total-row">
                <span>Tax (${selectedOrder.taxRate}%):</span>
                <span>€${selectedOrder.taxAmount?.toFixed(2)}</span>
              </div>
            ` : ''}
            <div class="receipt-total-row">
              <span>Delivery:</span>
              <span>€${selectedOrder.deliveryFee?.toFixed(2) || '0.00'}</span>
            </div>
            ${selectedOrder.serviceFee > 0 ? `
              <div class="receipt-total-row">
                <span>Service:</span>
                <span>€${selectedOrder.serviceFee?.toFixed(2)}</span>
              </div>
            ` : ''}
            ${selectedOrder.discountAmount > 0 ? `
              <div class="receipt-total-row">
                <span>Discount:</span>
                <span>-€${selectedOrder.discountAmount?.toFixed(2)}</span>
              </div>
            ` : ''}
            <div class="receipt-total-row" style="font-size: 13px; border-top: 1px solid #000; padding-top: 5px; margin-top: 5px;">
              <span><strong>TOTAL:</strong></span>
              <span><strong>€${((selectedOrder.total || 0) + (selectedOrder.walletAmount || 0)).toFixed(2)}</strong></span>
            </div>
          </div>

          <div class="receipt-footer">
            <div class="receipt-row"><strong>Payment:</strong> ${selectedOrder.paymentMethod || 'N/A'}</div>
            <div class="receipt-row"><strong>Status:</strong> ${selectedOrder.status}</div>
            ${selectedOrder.deliveryMethod ? `
              <div class="receipt-row"><strong>Method:</strong> ${selectedOrder.deliveryMethod === 'delivery' ? 'Delivery' : selectedOrder.deliveryMethod === 'pickup' ? 'Pickup' : 'Eat In'}</div>
            ` : ''}
            ${selectedOrder.deliveryDriver ? `
              <div class="receipt-row"><strong>Driver:</strong> ${selectedOrder.deliveryDriver.firstName || ''} ${selectedOrder.deliveryDriver.lastName || ''}</div>
            ` : ''}
            <div style="margin-top: 10px; font-size: 8px;">
              ${new Date().toLocaleString('en-GB')}
            </div>
          </div>
        </body>
      </html>
    `;
    printWindow.document.write(receiptContent);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const getPaymentStatusBadge = (status) => {
    const statusColors = {
      paid: { bg: '#27ae60', color: 'white' },
      pending: { bg: '#f39c12', color: 'white' },
      failed: { bg: '#e74c3c', color: 'white' },
      refunded: { bg: '#95a5a6', color: 'white' }
    };
    const colors = statusColors[status] || { bg: '#95a5a6', color: 'white' };
    return (
      <span 
        className="payment-status-badge" 
        style={{ 
          backgroundColor: colors.bg, 
          color: colors.color,
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          textTransform: 'capitalize'
        }}
      >
        {status || 'pending'}
      </span>
    );
  };

  const getOrderStatusBadge = (status) => {
    const statusColors = {
      delivered: { bg: '#27ae60', color: 'white' },
      pending: { bg: '#3498db', color: 'white' },
      confirmed: { bg: '#3498db', color: 'white' },
      preparing: { bg: '#9b59b6', color: 'white' },
      readyForPickup: { bg: '#e67e22', color: 'white' },
      outForDelivery: { bg: '#e67e22', color: 'white' },
      processing: { bg: '#f39c12', color: 'white' },
      cancelled: { bg: '#e74c3c', color: 'white' },
      'in-transit': { bg: '#9b59b6', color: 'white' }
    };
    const colors = statusColors[status] || { bg: '#95a5a6', color: 'white' };
    return (
      <span 
        className="order-status-badge" 
        style={{ 
          backgroundColor: colors.bg, 
          color: colors.color,
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          textTransform: 'capitalize'
        }}
      >
        {status}
      </span>
    );
  };

  const filteredOrders = statusFilter === 'all' 
    ? orders 
    : orders.filter(order => order.status === statusFilter);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / itemsPerPage));
  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredOrders.slice(start, start + itemsPerPage);
  }, [filteredOrders, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredOrders.length, itemsPerPage]);

  if (loading) {
    return (
      <div className="orders-container">
        <div className="loading-spinner">{t('admin.loading')}</div>
      </div>
    );
  }

  const venueOptions = [
    ...restaurants.map(r => ({ id: r._id, name: r.name, type: 'Restaurant' })),
    ...stores.map(s => ({ id: s._id, name: s.name, type: 'Store' }))
  ];

  return (
    <div className="orders-container">
      <div className="orders-header">
        <h2>{t('admin.orders')}</h2>
        <div className="status-filter">
          <label>{t('admin.filterByStatus')}</label>
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('admin.all')} {t('admin.orders')}</option>
            <option value="pending">{t('admin.pending')}</option>
            <option value="confirmed">{t('admin.confirmed')}</option>
            <option value="preparing">{t('admin.preparing')}</option>
            <option value="readyForPickup">{t('admin.readyForPickup') || 'Ready for pickup'}</option>
            <option value="outForDelivery">{t('admin.outForDelivery')}</option>
            <option value="delivered">{t('admin.delivered')}</option>
            <option value="cancelled">{t('admin.cancelled')}</option>
          </select>
        </div>
      </div>

      <div className="orders-filters">
        <div className="orders-filters-row">
          <div className="filter-group orders-search-group">
            <label>{t('admin.search')}</label>
            <div className="search-controls">
              <input
                type="text"
                className="orders-search-input"
                placeholder={t('admin.searchOrdersPlaceholder') || 'Order ID, customer name, restaurant...'}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applySearch()}
              />
              <button type="button" className="btn btn-primary btn-sm btn-search" onClick={applySearch}>
                {t('admin.search')}
              </button>
            </div>
          </div>
          <div className="orders-filters-controls">
            <div className="filter-group">
              <label>{t('admin.timePeriod')}</label>
              <select
                value={timeFilter}
                onChange={(e) => { setTimeFilter(e.target.value); setCurrentPage(1); }}
                className="filter-select"
              >
                <option value="">{t('admin.all')}</option>
                <option value="today">{t('admin.today')}</option>
                <option value="yesterday">{t('admin.yesterday')}</option>
                <option value="last7days">{t('admin.last7Days')}</option>
                <option value="last30days">{t('admin.last30Days')}</option>
                <option value="custom">{t('admin.customRange')}</option>
              </select>
            </div>
            {timeFilter === 'custom' && (
              <div className="filter-group date-range-group">
                <label>{t('admin.dateRange')}</label>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                <span>{t('admin.to')}</span>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} min={dateFrom} />
              </div>
            )}
            <div className="filter-group">
              <label>{t('admin.restaurant')}/{t('admin.store')}</label>
              <select
                value={restaurantId}
                onChange={(e) => { setRestaurantId(e.target.value); setCurrentPage(1); }}
                className="filter-select"
              >
                <option value="">{t('admin.all')}</option>
                {venueOptions.map((v) => (
                  <option key={v.id} value={v.id}>{v.type === 'Store' ? '🏪' : '🍽️'} {v.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group filter-group-clear">
              <label className="filter-group-spacer" htmlFor="orders-clear-filters" aria-hidden="true">&#8203;</label>
              <button id="orders-clear-filters" type="button" className="btn btn-secondary btn-sm btn-clear-filters" onClick={clearFilters}>
                {t('admin.clearFilters')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="orders-table-wrapper">
        <table className="orders-table">
          <thead>
            <tr>
              <th>{t('admin.orderNumber')}</th>
              <th>{t('admin.customer')}</th>
              <th>{t('admin.restaurant')}/{t('admin.store')}</th>
              <th>{t('admin.type')}</th>
              <th>{t('admin.paymentStatus')}</th>
              <th>{t('admin.total')}</th>
              <th>{t('admin.status')}</th>
              <th>{t('admin.date')}</th>
              <th>{t('admin.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-orders">
                  {t('admin.noOrders')}
                </td>
              </tr>
            ) : (
              paginatedOrders.map(order => (
                <tr key={order._id}>
                  <td className="order-number" data-label={t('admin.orderNumber')}>{order.orderNumber}</td>
                  <td data-label={t('admin.customer')}>
                    <div className="customer-info">
                      <div className="customer-name">
                        {order.customerName || (order.user ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() : '') || '—'}
                      </div>
                      <div className="customer-email">{order.customerEmail ?? order.user?.email ?? '—'}</div>
                    </div>
                  </td>
                  <td className="restaurant-name" data-label={`${t('admin.restaurant')}/${t('admin.store')}`}>{order.restaurantName || 'N/A'}</td>
                  <td data-label={t('admin.type')}>
                    <span className="type-badge">
                      {order.restaurantType === 'Store' ? '🏪 Store' : '🍽️ Restaurant'}
                    </span>
                  </td>
                  <td data-label={t('admin.paymentStatus')}>{getPaymentStatusBadge(order.paymentStatus)}</td>
                  <td className="order-total" data-label={t('admin.total')}>€{order.total?.toFixed(2)}</td>
                  <td data-label={t('admin.status')}>{getOrderStatusBadge(order.status)}</td>
                  <td className="order-date" data-label={t('admin.date')}>
                    {new Date(order.createdAt).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </td>
                  <td data-label={t('admin.actions')}>
                    <button 
                      onClick={() => handleView(order._id)} 
                      className="view-btn"
                    >
                      {t('admin.view')}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AdminPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={filteredOrders.length}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
      />

      {selectedOrder && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{t('admin.orderDetails')} - {selectedOrder.orderNumber}</h2>
              <button 
                className="close-btn" 
                onClick={() => setSelectedOrder(null)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="detail-section">
                <h3>{t('admin.customer')} {t('admin.basicInformation')}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>{t('admin.name')}:</label>
                    <span>{selectedOrder.customerName || (selectedOrder.user ? `${selectedOrder.user.firstName || ''} ${selectedOrder.user.lastName || ''}`.trim() : '') || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('admin.email')}:</label>
                    <span>{selectedOrder.customerEmail || selectedOrder.user?.email || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('admin.phone')}:</label>
                    <span>{selectedOrder.customerPhone ?? selectedOrder.user?.phoneNumber ?? 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>{t('admin.orderDetails')}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>{t('admin.restaurant')}/{t('admin.store')}:</label>
                    <span>{selectedOrder.restaurantName || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('admin.type')}:</label>
                    <span>{selectedOrder.restaurantType === 'Store' ? '🏪 ' + t('admin.store') : '🍽️ ' + t('admin.restaurant')}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('admin.orderStatus')}:</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {getOrderStatusBadge(selectedOrder.status)}
                      <select
                        value={selectedOrder.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        disabled={updatingStatus}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '1px solid #ddd',
                          fontSize: '13px',
                          cursor: updatingStatus ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <option value="pending">{t('admin.pending')}</option>
                        <option value="confirmed">{t('admin.confirmed')}</option>
                        <option value="preparing">{t('admin.preparing')}</option>
                        <option value="readyForPickup">{t('admin.readyForPickup') || 'Ready for pickup'}</option>
                        <option value="outForDelivery">{t('admin.outForDelivery')}</option>
                        <option value="delivered">{t('admin.delivered')}</option>
                        <option value="cancelled">{t('admin.cancelled')}</option>
                      </select>
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>{t('admin.paymentStatus')}:</label>
                    <span>{getPaymentStatusBadge(selectedOrder.paymentStatus)}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('admin.paymentMethod')}:</label>
                    <span className="payment-method">{selectedOrder.paymentMethod || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('admin.orderDate')}:</label>
                    <span>{new Date(selectedOrder.createdAt).toLocaleString('en-GB')}</span>
                  </div>
                  {selectedOrder.status === 'cancelled' && selectedOrder.cancellationMessage && (
                    <div className="detail-item">
                      <label>{t('admin.cancellationReason')}:</label>
                      <span className="cancellation-message">{selectedOrder.cancellationMessage}</span>
                    </div>
                  )}
                </div>
              </div>

              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <div className="detail-section">
                  <h3>{t('admin.items')}</h3>
                  <div className="items-list">
                    {selectedOrder.items.map((item, index) => (
                      <div key={index} className="item-row">
                        <div className="item-info">
                          <span className="item-name">{item.name}</span>
                          {item.selectedVariations && (
                            <div className="item-variations">
                              {Array.isArray(item.selectedVariations) ? (
                                // Handle array format
                                item.selectedVariations.map((variation, vIdx) => (
                                  <div key={vIdx} className="variation-group">
                                    <span className="variation-name">{variation.name}:</span>
                                    <div className="variation-options">
                                      {variation.selectedOptions?.map((option, oIdx) => (
                                        <span key={oIdx} className="variation-option-badge">{option}</span>
                                      ))}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                // Handle object format (variation name as key)
                                Object.entries(item.selectedVariations).map(([variationName, selectedOptions], vIdx) => (
                                  <div key={vIdx} className="variation-group">
                                    <span className="variation-name">{variationName}:</span>
                                    <div className="variation-options">
                                      {Array.isArray(selectedOptions) ? (
                                        selectedOptions.map((option, oIdx) => (
                                          <span key={oIdx} className="variation-option-badge">{option}</span>
                                        ))
                                      ) : (
                                        <span className="variation-option-badge">{selectedOptions}</span>
                                      )}
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                          {((item.ingredients && item.ingredients.length > 0) || (item.selectedIngredients && item.selectedIngredients.length > 0)) && (
                            <div className="item-ingredients">
                              {item.selectedIngredients && item.selectedIngredients.length > 0 && (
                                <div className="item-ingredients-included">
                                  <span className="ingredients-label">{t('admin.ingredientsIncluded') || 'Ingredients (included)'}:</span>
                                  <span>{item.selectedIngredients.join(', ')}</span>
                                </div>
                              )}
                              {item.ingredients && item.ingredients.length > 0 && (() => {
                                const selected = item.selectedIngredients || [];
                                const removed = item.ingredients.filter((ing) => !selected.includes(ing));
                                return removed.length > 0 ? (
                                  <div className="item-ingredients-removed">
                                    <span className="ingredients-label">{t('admin.ingredientsRemoved') || 'Without'}:</span>
                                    <span className="ingredients-removed-list">{removed.join(', ')}</span>
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          )}
                        </div>
                        <div className="item-quantity">Qty: {item.quantity}</div>
                        <div className="item-price">€{item.price?.toFixed(2)}</div>
                        <div className="item-total">€{(item.price * item.quantity)?.toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="detail-section">
                <h3>{t('admin.pricingBreakdown')}</h3>
                <div className="pricing-breakdown">
                  <div className="pricing-row">
                    <span>Subtotal (before tax):</span>
                    <span>€{selectedOrder.subtotalBeforeTax?.toFixed(2) || '0.00'}</span>
                  </div>
                  {selectedOrder.taxAmount && (
                    <div className="pricing-row">
                      <span>Tax ({selectedOrder.taxRate}%):</span>
                      <span>€{selectedOrder.taxAmount?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="pricing-row">
                    <span>Delivery Fee:</span>
                    <span>€{selectedOrder.deliveryFee?.toFixed(2) || '0.00'}</span>
                  </div>
                  {selectedOrder.serviceFee !== undefined && selectedOrder.serviceFee !== null && selectedOrder.serviceFee > 0 && (
                    <div className="pricing-row">
                      <span>Service Fee {selectedOrder.serviceFeeRate ? `(${selectedOrder.serviceFeeRate.toFixed(2)}%)` : ''}:</span>
                      <span>€{selectedOrder.serviceFee?.toFixed(2)}</span>
                    </div>
                  )}
                  {selectedOrder.driverDeliveryIncome !== undefined && selectedOrder.driverDeliveryIncome !== null && selectedOrder.driverDeliveryIncome > 0 && (
                    <div className="pricing-row" style={{ color: '#2196F3', fontWeight: 'bold' }}>
                      <span>Driver Delivery Income {selectedOrder.deliveryDistance ? `(${selectedOrder.deliveryDistance.toFixed(2)} km)` : ''}:</span>
                      <span>€{selectedOrder.driverDeliveryIncome?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="pricing-row total-row">
                    <span>Total:</span>
                    <span>€{selectedOrder.total?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {selectedOrder.deliveryAddress && (
                <div className="detail-section">
                  <h3>{t('admin.deliveryAddress')}</h3>
                  <div className="address-info">
                    <p>{selectedOrder.deliveryAddress.street}</p>
                    {selectedOrder.deliveryAddress.addressDetails && <p>{selectedOrder.deliveryAddress.addressDetails}</p>}
                    <p>{selectedOrder.deliveryAddress.city}, {selectedOrder.deliveryAddress.zipCode}</p>
                    {selectedOrder.deliveryAddress.country && (
                      <p>{selectedOrder.deliveryAddress.country}</p>
                    )}
                    {selectedOrder.deliveryAddress.coordinates?.lat != null && selectedOrder.deliveryAddress.coordinates?.lng != null && (
                      <p className="delivery-pin-info">
                        <a
                          href={`https://www.google.com/maps?q=${selectedOrder.deliveryAddress.coordinates.lat},${selectedOrder.deliveryAddress.coordinates.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('admin.openExactLocationInMaps') || 'Open exact delivery location in Google Maps'}
                        </a>
                        {' — '}
                        {t('admin.exactLocationSetByCustomer') || 'Exact location set by customer'}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {(selectedOrder.restaurantInstructions || selectedOrder.driverInstructions) && (
                <div className="detail-section">
                  <h3>{t('admin.customerInstructionsSection')}</h3>
                  <div className="detail-grid">
                    {selectedOrder.restaurantInstructions && (
                      <div className="detail-item detail-item-full">
                        <label>{t('admin.restaurantInstructions')}:</label>
                        <span className="instructions-text">{selectedOrder.restaurantInstructions}</span>
                      </div>
                    )}
                    {selectedOrder.driverInstructions && (
                      <div className="detail-item detail-item-full">
                        <label>{t('admin.driverInstructions')}:</label>
                        <span className="instructions-text">{selectedOrder.driverInstructions}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedOrder.deliveryDriver && (
                <div className="detail-section">
                  <h3>{t('admin.deliveryDriver')}</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>{t('admin.name')}:</label>
                      <span>{selectedOrder.deliveryDriver.firstName} {selectedOrder.deliveryDriver.lastName}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <div className="modal-footer-left">
                <button 
                  onClick={() => printA4()} 
                  className="print-btn"
                >
                  {t('admin.printA4')}
                </button>
                <button 
                  onClick={() => printReceipt()} 
                  className="print-btn receipt-btn"
                >
                  {t('admin.printReceipt')}
                </button>
              </div>
              <button 
                onClick={() => setSelectedOrder(null)} 
                className="close-modal-btn"
              >
                {t('admin.close')}
              </button>
            </div>
            
            {selectedOrder && (
              <div className="changelog-section">
                <ChangeLog entityType="Order" entityId={selectedOrder._id} />
              </div>
            )}
          </div>
        </div>
      )}

      <CancelOrderModal
        show={showCancelModal}
        orderNumber={selectedOrder?.orderNumber}
        onConfirm={handleCancelOrderConfirm}
        onClose={() => setShowCancelModal(false)}
      />
    </div>
  );
};

export default Orders;
